/* RESPONSIBLE TEAM: team-inbox-experience */
import Service, { inject as service } from '@ember/service';
import { request } from 'embercom/lib/inbox/requests';
import type Session from 'embercom/services/session';
import { tracked } from '@glimmer/tracking';

export interface CustomEmoji {
  identifier: string;
  src: string;
  alt: string;
  width?: number;
  height?: number;
  isCustomEmoji: boolean;
}

export default class CustomEmojiService extends Service {
  @service declare session: Session;

  @tracked private _customEmojis: CustomEmoji[] | null = null;
  @tracked private _isLoading = false;
  private _loadingPromise: Promise<CustomEmoji[]> | null = null;
  private _emojiMap: Map<string, CustomEmoji> | null = null;

  get customEmojis(): CustomEmoji[] {
    return this._customEmojis || [];
  }

  get isLoaded(): boolean {
    return this._customEmojis !== null;
  }

  get isLoading(): boolean {
    return this._isLoading;
  }

  async loadCustomEmojis(): Promise<CustomEmoji[]> {
    if (this._customEmojis) {
      return this._customEmojis;
    }

    if (this._loadingPromise) {
      return this._loadingPromise;
    }

    this._isLoading = true;
    this._loadingPromise = this._fetchCustomEmojis();

    try {
      let emojis = await this._loadingPromise;
      this._customEmojis = emojis;
      this._emojiMap = new Map(emojis.map((emoji) => [emoji.identifier, emoji]));
      return emojis;
    } finally {
      this._isLoading = false;
      this._loadingPromise = null;
    }
  }

  private async _fetchCustomEmojis(): Promise<CustomEmoji[]> {
    try {
      let appId = this.session.workspace.id;
      let response = await request(`/ember/emojis?app_id=${appId}`);
      let data = await response.json();

      if (data && data.emojis && Array.isArray(data.emojis)) {
        return data.emojis.map((emoji: any) => ({
          identifier: emoji.emoji_name.replace(/:/g, ''),
          src: emoji.small_asset_url,
          alt: emoji.emoji_name,
          isCustomEmoji: true,
        }));
      }

      return [];
    } catch (error) {
      console.error('Failed to fetch custom emojis:', error);
      return [];
    }
  }

  getCustomEmoji(identifier: string): CustomEmoji | undefined {
    return this._emojiMap?.get(identifier);
  }

  getAllCustomEmojis(): CustomEmoji[] {
    return this.customEmojis;
  }

  isCustomEmoji(identifier: string): boolean {
    return this._emojiMap?.has(identifier) ?? false;
  }
}

declare module '@ember/service' {
  interface Registry {
    'custom-emoji-service': CustomEmojiService;
    customEmojiService: CustomEmojiService;
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'custom-emoji': CustomEmojiService;
  }
}
